import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import Layout from "../components/Layout";
import { ArrowUpRight, Quote } from "lucide-react";
import { testimonialsFr, testimonialsEn } from "../data/testimonials";

// Mountain editorial palette — adventure, ascent, light
const IMG_HERO = "/hero-alpinist.jpg";
const IMG_COACHING = "/coaching-individuel.jpg";
const IMG_OUTPLACEMENT = "/outplacement.jpg";
const IMG_TEAM = "/coaching-equipe.jpg";
const IMG_FORMATION = "/formation.jpg";
const IMG_FINAL = "/contact-mountain.jpg";

// Client logos served from /public/logos. Order kept alphabetical for visual neutrality.
const CLIENT_LOGOS = [
  { name: "Arkema",      src: "/logos/arkema.png" },
  { name: "AstraZeneca", src: "/logos/astrazeneca.svg" },
  { name: "Equans",      src: "/logos/equans.png" },
  { name: "Faurecia",    src: "/logos/faurecia.png" },
  { name: "Forvia",      src: "/logos/forvia.svg" },
  { name: "Ingenico",    src: "/logos/ingenico.png" },
  { name: "KPMG",        src: "/logos/kpmg.svg" },
  { name: "Sodexo",      src: "/logos/sodexo.png" },
];

export default function Home() {
  const { t, lang } = useLang();
  const testimonials = lang === "fr" ? testimonialsFr : testimonialsEn;

  const services = [
    { key: "individual", to: "/coaching", img: IMG_COACHING },
    { key: "outplacement", to: "/outplacement", img: IMG_OUTPLACEMENT },
    { key: "team", to: "/equipes", img: IMG_TEAM, disabled: true },
    { key: "formation", to: "/formation", img: IMG_FORMATION, disabled: true },
  ];

  return (
    <Layout>
      {/* HERO — Mountain trail toward summit & light */}
      <section data-testid="hero" className="relative min-h-[100svh] overflow-hidden bg-ivory">
        <div className="absolute inset-0">
          <img src={IMG_HERO} alt="" className="absolute inset-0 w-full h-full object-cover editorial-img animate-subtle-pan" />
          <div className="absolute inset-0 bg-gradient-to-b from-ivory/85 via-ivory/20 to-ivory/65" />
          <div className="absolute inset-0 bg-gradient-to-r from-ivory/55 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 pt-24 md:pt-28 pb-14 min-h-[100svh] flex flex-col">
          <div className="grid grid-cols-12 gap-6">
            <h1 className="col-span-12 lg:col-span-10 font-serif text-navy text-[14vw] md:text-[9vw] lg:text-[6.5vw] leading-[0.92] tracking-[-0.03em] font-light drop-shadow-sm">
              <span className="italic font-normal text-brand">{t.home.title_a}</span>{" "}
              <span className="block md:inline">{t.home.title_b}</span>{" "}
              <span className="block">{t.home.title_c}</span>
            </h1>
          </div>

          <div className="grid grid-cols-12 gap-6 mt-auto pt-12">
            <p className="col-span-12 md:col-span-7 text-ink/85 text-base md:text-lg leading-relaxed font-light max-w-2xl">
              {t.home.intro}
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES PILLARS — no eyebrow / no title, only cards */}
      <section data-testid="services-section" className="py-16 md:py-24 px-6 md:px-10 max-w-[1500px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10">
          {services.map((s, i) => {
            const v = t.home.services[s.key];
            if (!v) return null;
            const CardInner = (
              <>
                <div className="flex items-start justify-between mb-6">
                  {!s.disabled && (
                    <ArrowUpRight className="ml-auto opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500" size={20} />
                  )}
                </div>
                <div className="aspect-[16/9] mb-6 overflow-hidden">
                  <img src={s.img} alt="" className={`w-full h-full object-cover editorial-img transition-transform duration-1000 ${s.disabled ? "opacity-90" : "group-hover:scale-105"}`} loading="lazy" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-light tracking-tight leading-tight mb-3">{v.title}</h3>
                <p className="text-ink/70 leading-relaxed max-w-md text-sm md:text-base">{v.desc}</p>
              </>
            );
            if (s.disabled) {
              return (
                <div
                  key={s.key}
                  data-testid={`service-card-${s.key}`}
                  aria-disabled="true"
                  className="group relative bg-ivory p-8 md:p-10 overflow-hidden cursor-not-allowed select-none"
                >
                  {CardInner}
                </div>
              );
            }
            return (
              <Link
                key={s.key}
                to={s.to}
                data-testid={`service-card-${s.key}`}
                className="group relative bg-ivory p-8 md:p-10 hover:bg-white transition-colors duration-500 overflow-hidden"
              >
                {CardInner}
              </Link>
            );
          })}
        </div>
      </section>

      {/* CONVICTION — original Home conviction, restored */}
      <section data-testid="manifesto-section" className="bg-navy text-ivory py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-[1500px] mx-auto grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <h2 className="font-serif text-2xl md:text-3xl text-ivory leading-tight font-light">{t.home.manifesto_title}</h2>
          </div>
          <div className="col-span-12 md:col-span-8 md:col-start-5">
            <p className="font-serif text-xl md:text-3xl leading-[1.3] font-light italic text-ivory/95">{`« ${t.home.manifesto_body} »`}</p>
            <div className="hairline bg-brand w-16 mt-8 opacity-100"></div>
          </div>
        </div>
      </section>

      {/* NOTRE ADN — title-only, bold orange */}
      <section data-testid="adn-section" className="py-14 md:py-16 px-6 md:px-10 max-w-[1500px] mx-auto">
        <div className="mb-8">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight font-semibold text-brand">{t.home.adn_eyebrow}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-black/10 mt-6">
          {t.home.adn_items.map((a, i) => (
            <div key={i} className="bg-ivory p-5 md:p-7 lift" data-testid={`adn-${i + 1}`}>
              <p className="text-ink/85 text-sm leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* NOS VALEURS — from former About page, no eyebrow */}
      <section data-testid="values-section" className="py-14 md:py-20 px-6 md:px-10 max-w-[1500px] mx-auto">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <h2 className="font-serif text-3xl md:text-4xl leading-tight font-light text-navy">{t.about.values_title}</h2>
          </div>
          <div className="col-span-12 md:col-span-8 md:col-start-5 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12 mt-6 md:mt-0">
            {t.about.values.map((v, i) => (
              <div key={i} data-testid={`value-${i}`}>
                <h3 className="font-serif text-2xl mb-2 font-light text-navy">{v.t}</h3>
                <p className="text-ink/70 text-sm leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNATURE — orfèvres tagline + 20+ years statement */}
      <section data-testid="figures-section" className="bg-sand py-14 md:py-20 px-6 md:px-10">
        <div className="max-w-[1500px] mx-auto grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight font-light text-navy">{t.home.signature_title}</h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p data-testid="signature-body" className="font-serif text-lg md:text-xl leading-relaxed text-ink/85 font-light italic">
              {t.home.signature_body}
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — single-line slow-scroll ribbon */}
      <section data-testid="testimonials-section" className="py-16 md:py-20 overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 mb-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand">— {t.home.testimonials_eyebrow || "Ils témoignent"}</div>
        </div>
        <div className="relative">
          <div className="flex gap-4 md:gap-6 animate-marquee-slow whitespace-nowrap will-change-transform">
            {[...Array(2)].map((_, dup) => (
              <div key={dup} className="flex items-center gap-4 md:gap-6 flex-none" aria-hidden={dup === 1}>
                {testimonials.map((t2, i) => (
                  <article
                    key={`${dup}-${i}`}
                    data-testid={dup === 0 ? `testimonial-${i}` : undefined}
                    className="flex-none w-[460px] md:w-[540px] bg-ivory border border-black/10 px-7 py-6 whitespace-normal"
                  >
                    <div className="flex items-start gap-4">
                      <Quote size={18} className="text-brand flex-none mt-1" />
                      <div className="min-w-0">
                        <p className="font-serif text-base md:text-lg leading-snug font-light text-ink/90 italic line-clamp-3">{`« ${t2.quote} »`}</p>
                        <div className="mt-3 text-[11px] font-mono uppercase tracking-[0.16em] text-navy">{t2.author}</div>
                        <div className="text-[11px] text-ink/55 mt-0.5 truncate">{t2.context}</div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGOS — scrolling marquee */}
      <section data-testid="clients-section" className="bg-ivory border-y border-black/10 py-12 overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 mb-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand">— {t.home.clients_eyebrow}</div>
        </div>
        <div className="relative">
          <div className="flex gap-16 md:gap-24 animate-marquee whitespace-nowrap will-change-transform items-center">
            {[...Array(2)].map((_, dup) => (
              <div key={dup} className="flex items-center gap-16 md:gap-24 flex-none" aria-hidden={dup === 1}>
                {CLIENT_LOGOS.map((c, i) => (
                  <img
                    key={`${dup}-${i}`}
                    src={c.src}
                    alt={c.name}
                    title={c.name}
                    data-testid={dup === 0 ? `client-logo-${i}` : undefined}
                    loading="lazy"
                    className="h-10 md:h-12 w-auto max-w-[160px] md:max-w-[200px] object-contain opacity-90 hover:opacity-100 transition-opacity duration-300"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta" className="relative overflow-hidden">
          <div className="relative h-[18vh] min-h-[140px] max-h-[200px]">
            <img src={IMG_FINAL} alt="" className="absolute inset-0 w-full h-full object-cover editorial-img" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
            <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-row items-center justify-between gap-6">
              <h2 className="font-serif text-ivory text-xl md:text-2xl leading-[1] font-light tracking-tight max-w-2xl">
                {t.home.final_title}
              </h2>
              <Link
                to="/contact"
                data-testid="final-cta-btn"
                className="group inline-flex items-center gap-2 bg-brand hover:bg-brandDark text-white px-3 py-2 transition-colors flex-none"
              >
                <span className="text-[10px] tracking-[0.22em] uppercase font-mono">{t.home.final_cta}</span>
                <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </div>
      </section>
    </Layout>
  );
}
